import { colors } from "theme/foundations/colors";
import { STATUSFIELD_VALUE_LABEL } from "../IssbDataInput/components/AdditionalStatuses";
import { equalsIgnoreCase, includesIgnoreCase } from "../../../utils/text-utils";

// Constants

export const stageColors = colors.stages;

// Utils

export const calculateLimeGradientColor = (value, min, max) => {
  const limeShade = Object.values(colors.limeShade);
  const range = max - min;

  let textColor = "#000000"; // Default text color

  if (range <= 0) {
    // Handle edge case where min and max are the same
    return { textColor, color: limeShade[0] || "#000000" };
  }

  // Normalize value to a 0-1 range and scale to limeShade length
  const normalizedValue = (value - min) / range;

  const index = Math.floor(normalizedValue * limeShade.length);

  textColor = index < 5 ? "#000000" : "#ffffff"; // Change text color based on index

  return { textColor, color: limeShade[index] || limeShade[limeShade.length - 1] };
};

export const removeWrapperAndGetListData = (data, groupBy = "NONE") => {
  return data?.data?.[groupBy] || [];
};

export const filterQuestionsByOwnerOrAssignee = (questionList = [], email = null) => {
  // Filter the questions by assignee or owner if email is provided
  return questionList.filter((question) => {
    if (!email) {
      return true;
    }
    return equalsIgnoreCase(question.owner, email) || includesIgnoreCase(question.assignees, email);
  });
};

export const getQuestionGroupList = (questionList) => {
  return questionList.reduce((acc, question) => {
    const group = question?.questionGroup;
    if (!acc.includes(group)) {
      acc.push(group);
    }
    return acc;
  }, []);
};

export const filterQuestionsByGroup = (questionList, groupFilter) => {
  return questionList.filter((question) => groupFilter.includes(question?.questionGroup));
};

/**
 * Get the number of questions per stage
 * @param {*} questionList
 * @returns {Object} {stage-name: {count: number, stageType: string}}
 */
export const getStages = (questionList) => {
  return questionList.reduce((acc, question) => {
    const stage = question?.stage;
    const stageType = question?.stageType;
    if (!acc[stage]) {
      acc[stage] = { count: 0, stageType: stageType };
    }
    acc[stage].count++;
    return acc;
  }, {});
};

// Dashboard data calculations start here

export const getNumOwned = (questionList, email) => {
  return questionList?.filter((q) => equalsIgnoreCase(q.owner, email)).length;
};

export const getNumAssigned = (questionList, email) => {
  return questionList?.filter((q) => includesIgnoreCase(q.assignees, email)).length;
};

/**
 *
 * @param {*} questionList
 * @param {*} email Filter Qs by this
 * @returns [{id: stage-name, value:number, color}]
 */
export const getStageDashboardData = (questionList) => {
  // Initialize a map to count the number of questions per stage
  const stageCounts = getStages(questionList);

  // Convert the stageCounts map to the required format
  return Object.keys(stageCounts).map((stage) => ({
    id: stage,
    value: stageCounts[stage].count,
    color: stageColors[stageCounts[stage].stageType] || "#000000",
  }));
};

export const getGroupDashboardData = (questionList) => {
  // Initialize a map to count the number of questions per group and stage
  const groupStageCounts = questionList.reduce((acc, question) => {
    const group = question?.questionGroup;
    const stage = question?.stage;
    const stageType = question?.stageType;
    if (!acc[group]) {
      acc[group] = {};
    }
    if (!acc[group][stage]) {
      acc[group][stage] = { count: 0, stageType: stageType };
    }
    acc[group][stage].count++;
    return acc;
  }, {});

  // Convert the groupStageCounts map to the required format
  return Object.keys(groupStageCounts).map((group) => ({
    id: group,
    data: Object.keys(groupStageCounts[group]).map((stage) => ({
      x: stage,
      y: groupStageCounts[group][stage].count,
      color: stageColors[groupStageCounts[group][stage].stageType] || "#000000",
    })),
  }));
};

/**
 * a condensed list of stage types. so this labels but also merges some stage types
 */
export const STAGETYPE_LABEL = {
  START: "Not Started",
  AI_QUEUE: "Not Started",
  EXPERT_REVIEW: "Not Started",
  PENDING: "Not Started",
  IN_PROGRESS: "In Progress",
  INTERNAL_REVIEW: "Internal Review", // Note: the design doc is a bit confused on how click-approved -> AUDIT="Ready for Audit" works
  AUDIT: "Audit",
  COMPLETE: "Complete",
};

/**
 *
 * @param {Question[]} questionList
 * @param {??} groupFilter
 * @returns [{ id: stageType, data: [{x: confidence, y: count, color: color-by-count}] }]
 */
export const getGapAnalysisDashboardData = (questionList, groupFilter = []) => {
  const possibleValues = STATUSFIELD_VALUE_LABEL;
  const filteredQuestions = filterQuestionsByGroup(questionList, groupFilter);

  // Initialize and update the data with zero values for all possible values
  const STAGETYPES_CONDENSED = [...new Set(Object.values(STAGETYPE_LABEL))];
  const CONFIDENCE_VALUES = Object.keys(possibleValues.confidence);
  const countBy_stageTypeCondensed_confidence = STAGETYPES_CONDENSED.reverse().reduce((acc, stageType) => {
    acc[stageType] = CONFIDENCE_VALUES.reduce((innerAcc, confidence) => {
      innerAcc[confidence] = 0;
      return innerAcc;
    }, {});
    return acc;
  }, {});

  // Populate the initialized data with actual values from questionList
  filteredQuestions.forEach((question) => {
    const stageType = STAGETYPE_LABEL[question.stageType];
    const confidence = question.confidence;
    if (
      countBy_stageTypeCondensed_confidence[stageType] &&
      countBy_stageTypeCondensed_confidence[stageType][confidence] !== undefined
    ) {
      countBy_stageTypeCondensed_confidence[stageType][confidence]++;
    }
  });

  const maxValue = Math.max(
    ...Object.values(countBy_stageTypeCondensed_confidence).flatMap((stageType) => Object.values(stageType))
  );

  const minValue = Math.min(
    ...Object.values(countBy_stageTypeCondensed_confidence).flatMap((stageType) => Object.values(stageType))
  );
  const data = STAGETYPES_CONDENSED.map((stageTypeCondensed) => ({
    id: stageTypeCondensed,
    data: Object.keys(countBy_stageTypeCondensed_confidence[stageTypeCondensed]).map((confidence) => {
      const count = countBy_stageTypeCondensed_confidence[stageTypeCondensed][confidence];
      const calculatedColor = calculateLimeGradientColor(count, minValue, maxValue);

      return {
        x: confidence,
        y: count,
        color: calculatedColor.color,
        textColor: calculatedColor.textColor,
      };
    }),
  }));
  return data;
};
